import { resolvePressChannelSlot } from "helpers/pressCalculationBase";
import { SmallBody, SmallBodyHighlight } from "helpers/TypographyComponents";
import { t } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ChannelType } from "../../store/types";
import type { IChannel } from "./types";

/**
 * Former channel-level milestone base, derived from type + usesAlternativeMilestoneAmount.
 * Returns null when the channel has no milestones (or type is unset on post-revamp channels).
 */
export const getLegacyMilestoneBaseTypeLabel = (
    channel: Pick<IChannel, "hasMilestones" | "type" | "usesAlternativeMilestoneAmount">,
): string | null => {
    if (!channel.hasMilestones || channel.type == null) return null;

    if (channel.type === ChannelType.QUANTITY_BASED) {
        return t("NewChannel.Quantity");
    }

    // Amount-based: alternative flag means milestones used quantity instead of CA.
    return channel.usesAlternativeMilestoneAmount ? t("NewChannel.Quantity") : t("NewChannel.Amount");
};

/**
 * Format Data for Press companies. Channel name has to be "Forfait" or "Feuillet".
 * Keeps `pressSlot` from the raw DB name so article forms can pick folio/amount without channel.type.
 */
export const useFormatChannels = (channels: IChannel[], portalType: string) => {
    return useMemo(() => {
        if (!["press", "audiovisual"].includes(portalType)) {
            return channels;
        }

        return (
            channels?.map((channel) => {
                const pressSlot = resolvePressChannelSlot(channel);
                let name = t("NewChannel.Folio");
                if (pressSlot === "amount") name = t("NewChannel.Package");
                if (pressSlot === "minimum_guaranteed") name = t("NewChannel.MinimumGuarantee");

                return {
                    ...channel,
                    name,
                    pressSlot,
                };
            }) ?? []
        );
    }, [channels, portalType]);
};

/**
 * Component ca render delete channel modal description
 * @param channel Channel to delete
 * @returns
 */
export const DeleteChannelDescription = ({ channel }: { channel: IChannel | undefined }): React.ReactNode | null => {
    const [t] = useTranslation();

    if (!channel) return null;
    if (!channel.hasTerm && !channel.hasTermTemplate) return null;

    return (
        <>
            {!!channel.hasTerm && (
                <>
                    <SmallBodyHighlight color="grey">{t("Channels.AssociatedContracts")}</SmallBodyHighlight>
                    <SmallBody color="grey">{t("Channels.LinkedData")}</SmallBody>
                </>
            )}
            {!!channel.hasTermTemplate && (
                <>
                    {!!channel.hasTerm && <br />}
                    <SmallBodyHighlight color="grey">{t("Channels.AssociatedTermTemplates")}</SmallBodyHighlight>
                    <SmallBody color="grey">{t("Channels.AssociatedTermTemplatesDesc")}</SmallBody>
                </>
            )}
        </>
    );
};
